/* =========================================================
   Piggery Studio — Preview Tool  (app.js)
   ========================================================= */

// ── State ────────────────────────────────────────────────
let map = null;
let currentTheme = THEMES.starlit_depth;
let currentCity    = '';
let currentCountry = '';
let currentLat     = null;
let currentLon     = null;
let searchTimer    = null;
let mapReady       = false;
let currentSize    = { label: 'A3', w: 29.7, h: 42, ratio: 0.7071 };
let buildingDetailPreview = false;

// Typography state
const typo = {
  font:          'Inter',
  colour:        null,   // null = use theme default
  cityScale:     1.0,
  subScale:      1.0,
  showCoords:    true,
  showCountry:   true,
  showAttrib:    true,
  customCity:    '',
  customCountry: '',
};

// ── DOM refs ─────────────────────────────────────────────
const citySearch     = document.getElementById('city-search');
const searchClear    = document.getElementById('search-clear');
const searchResults  = document.getElementById('search-results');
const themeGrid      = document.getElementById('theme-grid');
const themeNameDisp  = document.getElementById('theme-name-display');
const messageInput   = document.getElementById('message-input');
const zoomSlider     = document.getElementById('zoom-slider');
const zoomValue      = document.getElementById('zoom-value');
const copyBtn        = document.getElementById('copy-btn');
const placeholder    = document.getElementById('map-placeholder');
const statusBar      = document.getElementById('status-bar');
const copyToast      = document.getElementById('copy-toast');

// ── Map style builder ────────────────────────────────────
function getBuildingOutlineColor(theme) {
  return theme.road_tertiary || theme.text || theme.bg;
}

function buildMapStyle(theme) {
  const t = theme;
  return {
    version: 8,
    sources: {
      ofm: {
        type: 'vector',
        url: 'https://tiles.openfreemap.org/planet'
      }
    },
    layers: [
      // Land background
      {
        id: 'background',
        type: 'background',
        paint: { 'background-color': t.bg }
      },
      // Parks / green spaces
      {
        id: 'landuse-green',
        type: 'fill',
        source: 'ofm',
        'source-layer': 'landuse',
        filter: ['in', ['get', 'class'],
          ['literal', ['park', 'garden', 'grass', 'meadow', 'pitch',
                       'cemetery', 'allotments', 'forest', 'wood']]],
        paint: { 'fill-color': t.parks, 'fill-opacity': 1 }
      },
      {
        id: 'landcover-green',
        type: 'fill',
        source: 'ofm',
        'source-layer': 'landcover',
        filter: ['in', ['get', 'class'], ['literal', ['grass', 'wood', 'scrub', 'farmland']]],
        paint: { 'fill-color': t.parks, 'fill-opacity': 0.7 }
      },
      // Water
      {
        id: 'water-fill',
        type: 'fill',
        source: 'ofm',
        'source-layer': 'water',
        paint: { 'fill-color': t.water, 'fill-opacity': 1 }
      },
      {
        id: 'waterway-line',
        type: 'line',
        source: 'ofm',
        'source-layer': 'waterway',
        paint: {
          'line-color': t.water,
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 14, 2]
        }
      },
      // Buildings
      {
        id: 'building-fill',
        type: 'fill',
        source: 'ofm',
        'source-layer': 'building',
        minzoom: 11,
        paint: {
          'fill-color': t.buildings || t.bg,
          'fill-outline-color': getBuildingOutlineColor(t),
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.35, 12.5, 0.65, 14, 0.9]
        }
      },
      {
        id: 'building-detail-outline',
        type: 'line',
        source: 'ofm',
        'source-layer': 'building',
        minzoom: 12,
        layout: { visibility: 'none' },
        paint: {
          'line-color': getBuildingOutlineColor(t),
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.25, 14, 0.8, 16, 1.2],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0.18, 14, 0.35, 16, 0.5]
        }
      },
      // Roads — residential / minor (bottom of road stack)
      {
        id: 'road-minor',
        type: 'line',
        source: 'ofm',
        'source-layer': 'transportation',
        filter: ['in', ['get', 'class'],
          ['literal', ['minor', 'service', 'track', 'path', 'residential']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': t.road_residential,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.4, 14, 1.5]
        }
      },
      // Roads — tertiary
      {
        id: 'road-tertiary',
        type: 'line',
        source: 'ofm',
        'source-layer': 'transportation',
        filter: ['==', ['get', 'class'], 'tertiary'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': t.road_tertiary,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.7, 14, 2.5]
        }
      },
      // Roads — secondary
      {
        id: 'road-secondary',
        type: 'line',
        source: 'ofm',
        'source-layer': 'transportation',
        filter: ['==', ['get', 'class'], 'secondary'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': t.road_secondary,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 14, 3.5]
        }
      },
      // Roads — primary + trunk
      {
        id: 'road-primary',
        type: 'line',
        source: 'ofm',
        'source-layer': 'transportation',
        filter: ['in', ['get', 'class'], ['literal', ['primary', 'trunk']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': t.road_primary,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 14, 5]
        }
      },
      // Roads — motorway (top of road stack)
      {
        id: 'road-motorway',
        type: 'line',
        source: 'ofm',
        'source-layer': 'transportation',
        filter: ['==', ['get', 'class'], 'motorway'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': t.road_motorway,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2, 14, 7]
        }
      },
      // Railway lines
      {
        id: 'railway',
        type: 'line',
        source: 'ofm',
        'source-layer': 'transportation',
        filter: ['in', ['get', 'class'], ['literal', ['rail', 'transit']]],
        layout: { 'line-cap': 'butt', 'line-join': 'miter' },
        paint: {
          'line-color': t.road_tertiary || t.road_secondary,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 1.5],
          'line-dasharray': [3, 2]
        }
      }
    ]
  };
}

// ── Apply theme colours without reloading tiles ───────────
function applyTheme(theme) {
  if (!mapReady) return;
  const t = theme;

  const updates = [
    ['background',       'background-color', t.bg],
    ['landuse-green',    'fill-color',       t.parks],
    ['landcover-green',  'fill-color',       t.parks],
    ['water-fill',       'fill-color',       t.water],
    ['waterway-line',    'line-color',       t.water],
    ['building-fill',    'fill-color',       t.buildings || t.bg],
    ['building-fill',    'fill-outline-color', getBuildingOutlineColor(t)],
    ['building-detail-outline', 'line-color', getBuildingOutlineColor(t)],
    ['road-minor',       'line-color',       t.road_residential],
    ['road-tertiary',    'line-color',       t.road_tertiary],
    ['road-secondary',   'line-color',       t.road_secondary],
    ['road-primary',     'line-color',       t.road_primary],
    ['road-motorway',    'line-color',       t.road_motorway],
    ['railway',          'line-color',       t.road_tertiary || t.road_secondary],
  ];

  updates.forEach(([layer, prop, value]) => {
    try { map.setPaintProperty(layer, prop, value); } catch (_) {}
  });

  updateOverlay();
}

function setBuildingDetailPreview(active) {
  if (!mapReady) return;
  buildingDetailPreview = active;
  try {
    map.setLayoutProperty(
      'building-detail-outline',
      'visibility',
      active && layers.buildings ? 'visible' : 'none'
    );
  } catch (_) {}
}

// ── Poster text overlay ──────────────────────────────────
function updateOverlay() {
  const t      = currentTheme;
  const colour = typo.colour || t.text;
  const font   = `'${typo.font}', system-ui, sans-serif`;

  document.getElementById('poster-overlay').style.setProperty('--overlay-bg', t.gradient_color || t.bg);

  const cityEl    = document.querySelector('.poster-city');
  const countryEl = document.querySelector('.poster-country');
  const msgEl     = document.querySelector('.poster-message');
  const coordsEl  = document.querySelector('.poster-coords');
  const attribEl  = document.querySelector('.poster-attrib');

  // Colour + font
  [cityEl, countryEl, msgEl, coordsEl, attribEl].forEach(el => {
    el.style.color      = colour;
    el.style.fontFamily = font;
  });

  // City size scale
  cityEl.style.fontSize = `clamp(${14 * typo.cityScale}px, ${4 * typo.cityScale}cqw, ${42 * typo.cityScale}px)`;

  // Country / subtitle size scale
  countryEl.style.fontSize = `clamp(${7 * typo.subScale}px, ${1.5 * typo.subScale}cqw, ${16 * typo.subScale}px)`;

  // Content
  cityEl.textContent = typo.customCity || currentCity || 'Your City';

  const countryText = typo.customCountry
    || (currentCountry ? currentCountry.toUpperCase() : 'UNITED KINGDOM');
  countryEl.textContent    = countryText;
  countryEl.style.display  = typo.showCountry ? '' : 'none';

  const msg = messageInput.value.trim();
  msgEl.textContent = msg;
  msgEl.style.display = msg ? '' : 'none';

  if (typo.showCoords && currentLat !== null && currentLon !== null) {
    const latStr = Math.abs(currentLat).toFixed(4) + (currentLat >= 0 ? '° N' : '° S');
    const lonStr = Math.abs(currentLon).toFixed(4) + (currentLon >= 0 ? '° E' : '° W');
    coordsEl.textContent   = `${latStr}  ·  ${lonStr}`;
    coordsEl.style.display = '';
  } else {
    coordsEl.style.display = 'none';
  }

  attribEl.style.display = typo.showAttrib ? '' : 'none';
}

// ── Map init ──────────────────────────────────────────────
function initMap() {
  map = new maplibregl.Map({
    container: 'map',
    style: buildMapStyle(currentTheme),
    center: [-0.1278, 51.5074],   // London default
    zoom: 11,
    interactive: true,
    attributionControl: false,
    logoPosition: 'bottom-right',
    preserveDrawingBuffer: true   // required for canvas export
  });

  map.on('load', () => {
    mapReady = true;
    placeholder.classList.add('hidden');
    updateOverlay();
    updateAreaCoverage();
    updateCompass();
    pushHistory();
    loadSavedDesign();
    if (REVIEW_MODE) populateReviewDetails();
    // Ensure canvas fills its container on mobile (layout may shift after load)
    if (window.innerWidth <= 700) setTimeout(() => map.resize(), 50);
    setBuildingDetailPreview(true);
  });

  const disableBuildingDetailPreview = () => setBuildingDetailPreview(false);
  map.on('movestart', disableBuildingDetailPreview);
  map.on('zoomstart', disableBuildingDetailPreview);
  map.on('rotatestart', disableBuildingDetailPreview);
  map.on('pitchstart', disableBuildingDetailPreview);
  map.on('idle', () => setBuildingDetailPreview(true));

  map.on('moveend', () => {
    const c = map.getCenter();
    if (currentLat === null) {
      // User panned manually — update coords from map centre
      currentLat = c.lat;
      currentLon = c.lng;
      updateOverlay();
    }
  });
}

// ── Theme grid ────────────────────────────────────────────
function buildThemeGrid() {
  themeGrid.innerHTML = '';
  Object.values(THEMES).filter(t => t.available !== false).forEach(theme => {
    const el = document.createElement('div');
    el.className = 'theme-swatch';
    el.title = theme.name;
    el.dataset.id = theme.id;
    el.innerHTML = `
      <div class="theme-swatch-inner">
        <div class="swatch-bg" style="background:${theme.bg};flex:1"></div>
        <div class="swatch-road" style="background:${theme.road_motorway}"></div>
      </div>
      <div class="swatch-label">${theme.name}</div>`;
    el.addEventListener('click', () => selectTheme(theme.id));
    themeGrid.appendChild(el);
  });
  highlightActiveSwatch();
}

function highlightActiveSwatch() {
  document.querySelectorAll('.theme-swatch').forEach(el => {
    el.classList.toggle('active', el.dataset.id === currentTheme.id);
  });
  themeNameDisp.textContent = currentTheme.name;
}

function selectTheme(id) {
  const theme = THEMES[id];
  if (!theme) return;
  currentTheme = theme;
  highlightActiveSwatch();
  applyTheme(theme);
  syncRoadEditorToTheme(theme);
  // Sync text colour picker to theme default if no manual override
  if (!typo.colour) {
    const pick = document.getElementById('text-colour');
    if (pick) pick.value = theme.text;
  }
  // Update 3D building colour if active
  if (mapReady) {
    try { map.setPaintProperty('building-3d', 'fill-extrusion-color', theme.buildings || theme.bg); } catch (_) {}
  }
  scheduleSave();
  pushHistory();
}

// ── City search ───────────────────────────────────────────
citySearch.addEventListener('input', () => {
  const q = citySearch.value.trim();
  searchClear.style.display = q ? 'block' : 'none';
  clearTimeout(searchTimer);
  if (q.length < 2) { searchResults.style.display = 'none'; return; }
  searchTimer = setTimeout(() => nominatimSearch(q), 450);
});

citySearch.addEventListener('keydown', e => {
  if (e.key === 'Escape') clearSearch();
});

searchClear.addEventListener('click', clearSearch);

function clearSearch() {
  citySearch.value = '';
  searchClear.style.display = 'none';
  searchResults.style.display = 'none';
  clearTimeout(searchTimer);
}

document.addEventListener('click', e => {
  if (!e.target.closest('#city-wrap')) {
    searchResults.style.display = 'none';
  }
});

async function nominatimSearch(query) {
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&addressdetails=1&limit=5`;
    const res = await fetch(url, { headers: { 'User-Agent': 'PiggeryStudioPreview/1.0' } });
    const data = await res.json();
    showResults(data);
  } catch {
    searchResults.style.display = 'none';
  }
}

function showResults(results) {
  if (!results.length) { searchResults.style.display = 'none'; return; }

  searchResults.innerHTML = results.map(r => {
    const name    = r.display_name.split(',')[0];
    const country = r.address?.country || '';
    const type    = r.type || r.class || '';
    return `<div class="result-item"
                 data-lat="${r.lat}" data-lon="${r.lon}"
                 data-name="${name}" data-country="${country}"
                 data-display="${r.display_name}">
              <div class="result-name">${r.display_name.split(',').slice(0,3).join(', ')}</div>
              <div class="result-type">${type}</div>
            </div>`;
  }).join('');

  searchResults.querySelectorAll('.result-item').forEach(el => {
    el.addEventListener('click', () => selectLocation(el));
  });

  searchResults.style.display = 'block';
}

function selectLocation(el) {
  const lat     = parseFloat(el.dataset.lat);
  const lon     = parseFloat(el.dataset.lon);
  const name    = el.dataset.name;
  const country = el.dataset.country;

  currentCity    = name;
  currentCountry = country;
  currentLat     = lat;
  currentLon     = lon;

  citySearch.value = el.dataset.display.split(',').slice(0,2).join(', ');
  searchResults.style.display = 'none';
  searchClear.style.display = 'block';

  const zoom = parseFloat(zoomSlider.value);
  map.flyTo({ center: [lon, lat], zoom, duration: 1200, essential: true });

  updateOverlay();

  // Smart suggestions + save (functions defined later in file — called at runtime)
  if (typeof suggestThemes === 'function') suggestThemes({ display_name: el.dataset.display, type: el.dataset.type });
  scheduleSave();
  pushHistory();
}

// ── Zoom slider ───────────────────────────────────────────
zoomSlider.addEventListener('input', () => {
  const z = parseFloat(zoomSlider.value);
  zoomValue.textContent = z.toFixed(1);
  if (mapReady) map.setZoom(z);
});

map && map.on('zoom', () => {
  if (!mapReady) return;
  const z = map.getZoom();
  zoomSlider.value = z;
  zoomValue.textContent = z.toFixed(1);
});

// Keep slider in sync when user drags the map
function syncZoomSlider() {
  if (!map) return;
  map.on('zoom', () => {
    const z = map.getZoom();
    zoomSlider.value = z.toFixed(1);
    zoomValue.textContent = z.toFixed(1);
  });
}

// ── Message ───────────────────────────────────────────────
messageInput.addEventListener('input', updateOverlay);

// ── Gate internal-only buttons ────────────────────────────
const isInternal = new URLSearchParams(location.search).get('internal') === '1';
if (copyBtn) copyBtn.style.display = isInternal ? '' : 'none';
const printDownloadBtn = document.getElementById('print-download-btn');
if (printDownloadBtn) printDownloadBtn.style.display = isInternal ? '' : 'none';

// ── Copy settings for CLI ─────────────────────────────────
copyBtn.addEventListener('click', () => {
  const city    = currentCity    || citySearch.value.split(',')[0].trim() || 'London';
  const country = currentCountry || 'United Kingdom';
  const theme   = currentTheme.id;
  const msg     = messageInput.value.trim();
  const zoom    = parseFloat(zoomSlider.value);

  // Convert zoom to approximate distance for the Python CLI
  // zoom 9≈40km, 10≈20km, 11≈10km, 12≈5km, 13≈2.5km, 14≈1.2km
  const distM = Math.round(20000 * Math.pow(2, 11 - zoom));
  const dist  = Math.max(2000, Math.min(50000, distM));

  const lines = [
    `City: ${city}`,
    `Country: ${country}`,
    `Theme: ${theme}`,
    msg ? `Message: ${msg}` : null,
    `Distance: ${dist}`,
  ].filter(Boolean).join('\n');

  navigator.clipboard.writeText(lines).then(() => {
    showToast('Copied to clipboard — paste into fulfil_order');
    copyBtn.textContent = '✓ Copied';
    setTimeout(() => { copyBtn.textContent = 'Copy settings for fulfil_order'; }, 2500);
  }).catch(() => {
    // Fallback: show in a prompt
    prompt('Copy this:', lines);
  });
});

function showToast(msg) {
  copyToast.textContent = msg;
  copyToast.classList.add('visible');
  setTimeout(() => copyToast.classList.remove('visible'), 2800);
}

// ── Road editor ───────────────────────────────────────

// Road weight state (zoom-14 base values)
const roadWeights = {
  'road-motorway':  4,
  'road-primary':   3,
  'road-secondary': 2,
  'road-tertiary':  1.5,
  'road-minor':     0.8,
};

document.getElementById('road-editor-toggle').addEventListener('click', () => {
  const toggle = document.getElementById('road-editor-toggle');
  const panel  = document.getElementById('road-editor');
  const open   = !panel.classList.contains('hidden');
  panel.classList.toggle('hidden', open);
  toggle.classList.toggle('open', !open);
});

// Sync colour pickers to current theme when theme changes
function syncRoadEditorToTheme(theme) {
  document.querySelectorAll('.road-color-pick').forEach(pick => {
    const key = pick.dataset.key;
    if (theme[key]) pick.value = theme[key];
  });
}

// Colour pickers
document.querySelectorAll('.road-color-pick').forEach(pick => {
  pick.addEventListener('input', () => {
    if (!mapReady) return;
    const layer = pick.dataset.layer;
    const prop  = pick.dataset.prop || 'line-color';
    try {
      map.setPaintProperty(layer, prop, pick.value);
      // Also apply to sibling layers (waterway / landcover)
      if (layer === 'water-fill') {
        map.setPaintProperty('waterway-line', 'line-color', pick.value);
      }
      if (layer === 'landuse-green') {
        map.setPaintProperty('landcover-green', 'fill-color', pick.value);
      }
    } catch (_) {}
  });
});

// Weight sliders (only target road layers — requires data-layer attribute)
document.querySelectorAll('.road-weight[data-layer]').forEach(slider => {
  const valEl = slider.nextElementSibling;
  slider.addEventListener('input', () => {
    const layer = slider.dataset.layer;
    const w     = parseFloat(slider.value);
    valEl.textContent = w;
    roadWeights[layer] = w;
    if (!mapReady) return;
    // Build a zoom-interpolated expression scaled from the base weight
    const expr = ['interpolate', ['linear'], ['zoom'],
      10, w * 0.25,
      14, w
    ];
    try { map.setPaintProperty(layer, 'line-width', expr); } catch (_) {}
  });
});

// Reset button
document.getElementById('road-reset-btn').addEventListener('click', () => {
  applyTheme(currentTheme);
  syncRoadEditorToTheme(currentTheme);
  // Reset weight sliders to defaults
  const defaults = {
    'road-motorway': 4, 'road-primary': 3, 'road-secondary': 2,
    'road-tertiary': 1.5, 'road-minor': 0.8
  };
  document.querySelectorAll('.road-weight').forEach(slider => {
    const def = defaults[slider.dataset.layer];
    if (def !== undefined) {
      slider.value = def;
      slider.nextElementSibling.textContent = def;
      roadWeights[slider.dataset.layer] = def;
    }
  });
});

// ── Status messages ───────────────────────────────────────
function showStatus(msg, duration = 2000) {
  statusBar.textContent = msg;
  statusBar.classList.add('visible');
  if (duration > 0) setTimeout(() => statusBar.classList.remove('visible'), duration);
}

// ── Typography handlers ───────────────────────────────

document.getElementById('typo-toggle').addEventListener('click', () => {
  const panel = document.getElementById('typo-panel');
  const open  = !panel.classList.contains('hidden');
  panel.classList.toggle('hidden', open);
  document.getElementById('typo-toggle').classList.toggle('open', !open);
});

document.getElementById('font-select').addEventListener('change', e => {
  typo.font = e.target.value;
  updateOverlay();
});

document.getElementById('text-colour').addEventListener('input', e => {
  typo.colour = e.target.value;
  updateOverlay();
});

document.getElementById('text-colour-reset').addEventListener('click', () => {
  typo.colour = null;
  document.getElementById('text-colour').value = currentTheme.text;
  updateOverlay();
});

document.getElementById('city-size').addEventListener('input', e => {
  typo.cityScale = parseFloat(e.target.value);
  document.getElementById('city-size-val').textContent = typo.cityScale.toFixed(2) + '×';
  updateOverlay();
});

document.getElementById('sub-size').addEventListener('input', e => {
  typo.subScale = parseFloat(e.target.value);
  document.getElementById('sub-size-val').textContent = typo.subScale.toFixed(2) + '×';
  updateOverlay();
});

document.getElementById('custom-city').addEventListener('input', e => {
  typo.customCity = e.target.value.trim();
  updateOverlay();
});

document.getElementById('custom-country').addEventListener('input', e => {
  typo.customCountry = e.target.value.trim();
  updateOverlay();
});

document.getElementById('show-coords').addEventListener('change', e => {
  typo.showCoords = e.target.checked;
  updateOverlay();
});

document.getElementById('show-country').addEventListener('change', e => {
  typo.showCountry = e.target.checked;
  updateOverlay();
});

document.getElementById('show-attrib').addEventListener('change', e => {
  typo.showAttrib = e.target.checked;
  updateOverlay();
});

// ── Orientation ───────────────────────────────────────

document.querySelectorAll('.orient-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.orient-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    const ratio = parseFloat(btn.dataset.ratio);
    const frame = document.getElementById('poster-frame');
    if (btn.dataset.orient === 'landscape') {
      frame.style.aspectRatio = `${1 / ratio} / 1`;
      frame.style.height = `min(calc(100vh - 48px), calc((100vw - var(--sidebar-w) - 48px) * ${ratio}))`;
      frame.style.width  = '';
    } else {
      frame.style.aspectRatio = `1 / ${ratio}`;
      frame.style.height = `min(calc(100vh - 48px), calc((100vw - var(--sidebar-w) - 48px) * ${ratio}))`;
      frame.style.width  = '';
    }
    if (mapReady) map.resize();
  });
});

// ── Size picker ───────────────────────────────────────
function buildSizeGrid() {
  const btns = document.querySelectorAll('.size-btn');
  btns[0].classList.add('active'); // A3 default
  btns.forEach(btn => {
    btn.addEventListener('click', () => {
      btns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentSize = {
        label: btn.dataset.label,
        w:     parseFloat(btn.dataset.w),
        h:     parseFloat(btn.dataset.h),
        ratio: parseFloat(btn.dataset.ratio),
      };
      const frame = document.getElementById('poster-frame');
      const invRatio = 1 / currentSize.ratio;
      frame.style.aspectRatio = `1 / ${invRatio}`;
      frame.style.height = `min(calc(100vh - 48px), calc((100vw - var(--sidebar-w) - 48px) * ${invRatio}))`;
      if (mapReady) map.resize();
    });
  });
}

// ── Canvas protection ─────────────────────────────────
function addCanvasProtection() {
  const mapArea = document.getElementById('map-area');
  mapArea.addEventListener('contextmenu', e => e.preventDefault());
  mapArea.addEventListener('dragstart',   e => e.preventDefault());
  mapArea.addEventListener('keydown', e => {
    if ((e.ctrlKey || e.metaKey) && ['s','p'].includes(e.key.toLowerCase())) {
      e.preventDefault();
    }
  });
}

// ── Download watermarked preview ──────────────────────
document.getElementById('download-preview-btn').addEventListener('click', async () => {
  const btn = document.getElementById('download-preview-btn');
  btn.textContent = 'Generating…';
  btn.disabled = true;

  try {
    const mapCanvas = map.getCanvas();
    const w = mapCanvas.width;
    const h = mapCanvas.height;

    const canvas = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Map
    ctx.drawImage(mapCanvas, 0, 0);

    // Gradient fade
    const grad = ctx.createLinearGradient(0, h * 0.7, 0, h);
    const bg   = currentTheme.gradient_color || currentTheme.bg;
    grad.addColorStop(0, bg + '00');
    grad.addColorStop(1, bg + 'D9');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Poster text (respects typo state)
    const colour  = typo.colour || currentTheme.text;
    const font    = `'${typo.font}', system-ui, sans-serif`;
    const cityName = (typo.customCity || currentCity || 'Your City').toUpperCase();
    const countryName = (typo.customCountry || currentCountry || 'United Kingdom').toUpperCase();

    ctx.textAlign   = 'center';
    ctx.fillStyle   = colour;
    ctx.globalAlpha = 1;

    ctx.font = `700 ${Math.round(w * 0.07 * typo.cityScale)}px ${font}`;
    ctx.fillText(cityName, w / 2, h * 0.87);

    if (typo.showCountry) {
      ctx.font        = `400 ${Math.round(w * 0.025 * typo.subScale)}px ${font}`;
      ctx.globalAlpha = 0.85;
      ctx.fillText(countryName, w / 2, h * 0.905);
    }

    const msg = document.getElementById('message-input').value.trim();
    if (msg) {
      ctx.font        = `400 ${Math.round(w * 0.02)}px ${font}`;
      ctx.globalAlpha = 0.75;
      ctx.fillText(msg, w / 2, h * 0.935);
    }

    if (typo.showCoords && currentLat !== null) {
      const latStr = Math.abs(currentLat).toFixed(4) + (currentLat >= 0 ? '° N' : '° S');
      const lonStr = Math.abs(currentLon).toFixed(4) + (currentLon >= 0 ? '° E' : '° W');
      ctx.font        = `400 ${Math.round(w * 0.015)}px ${font}`;
      ctx.globalAlpha = 0.5;
      ctx.fillText(`${latStr}  ·  ${lonStr}`, w / 2, h * 0.958);
    }

    // Single centred diagonal watermark
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle   = '#ffffff';
    ctx.font        = `800 ${Math.round(w * 0.08)}px Inter, system-ui, sans-serif`;
    ctx.textAlign   = 'center';
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-35 * Math.PI / 180);
    ctx.fillText('PIGGERY PREVIEW', 0, 0);
    ctx.restore();
    ctx.globalAlpha = 1;

    // Attribution (respects toggle)
    if (typo.showAttrib) {
      ctx.textAlign   = 'right';
      ctx.font        = `400 ${Math.round(w * 0.016)}px Inter, system-ui, sans-serif`;
      ctx.fillStyle   = colour;
      ctx.globalAlpha = 0.35;
      ctx.fillText('PIGGERY | OSM', w * 0.98, h * 0.987);
      ctx.globalAlpha = 1;
    }

    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `piggery-preview-${(currentCity || 'map').toLowerCase().replace(/\s+/g, '-')}.png`;
      a.click();
      URL.revokeObjectURL(url);
      btn.textContent = '✓ Downloaded';
      setTimeout(() => { btn.textContent = 'Download Watermarked Preview'; btn.disabled = false; }, 2500);
    }, 'image/png');

  } catch (err) {
    console.error(err);
    btn.textContent = 'Download Watermarked Preview';
    btn.disabled = false;
    showToast('Could not generate preview — try again');
  }
});

// ── Download print file (internal) ───────────────────────
if (printDownloadBtn) {
  printDownloadBtn.addEventListener('click', async () => {
    const btn = printDownloadBtn;
    btn.textContent = 'Rendering…';
    btn.disabled = true;

    try {
      const isLandscape = document.querySelector('.orient-btn.active')?.dataset.orient === 'landscape';
      const PX_PER_CM   = 200 / 2.54; // 200 DPI
      const printW = Math.round((isLandscape ? currentSize.h : currentSize.w) * PX_PER_CM);
      const printH = Math.round((isLandscape ? currentSize.w : currentSize.h) * PX_PER_CM);

      const frame   = document.getElementById('poster-frame');
      const mapArea = document.getElementById('map-area');

      // Save current inline styles
      const saved = {
        frameW:  frame.style.width,
        frameH:  frame.style.height,
        frameAR: frame.style.aspectRatio,
        areaL:   mapArea.style.left,
        areaW:   mapArea.style.width,
        areaH:   mapArea.style.height,
      };

      // Move off-screen and resize to print dimensions
      mapArea.style.left   = '-99999px';
      mapArea.style.width  = printW + 'px';
      mapArea.style.height = printH + 'px';
      frame.style.width        = printW + 'px';
      frame.style.height       = printH + 'px';
      frame.style.aspectRatio  = '';

      map.resize();
      await new Promise(resolve => map.once('idle', resolve));

      const mapCanvas = map.getCanvas();
      const w = mapCanvas.width;
      const h = mapCanvas.height;

      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      // Map
      ctx.drawImage(mapCanvas, 0, 0);

      // Gradient fade (same as preview)
      const bg = currentTheme.gradient_color || currentTheme.bg;
      const grad = ctx.createLinearGradient(0, h * 0.7, 0, h);
      grad.addColorStop(0, bg + '00');
      grad.addColorStop(1, bg + 'D9');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Poster text
      const colour   = typo.colour || currentTheme.text;
      const font     = `'${typo.font}', system-ui, sans-serif`;
      const cityName = (typo.customCity    || currentCity    || 'Your City').toUpperCase();
      const cntryName= (typo.customCountry || currentCountry || '').toUpperCase();

      ctx.textAlign = 'center';
      ctx.fillStyle = colour;
      ctx.globalAlpha = 1;

      ctx.font = `700 ${Math.round(w * 0.07 * typo.cityScale)}px ${font}`;
      ctx.fillText(cityName, w / 2, h * 0.87);

      if (typo.showCountry && cntryName) {
        ctx.font        = `400 ${Math.round(w * 0.025 * typo.subScale)}px ${font}`;
        ctx.globalAlpha = 0.85;
        ctx.fillText(cntryName, w / 2, h * 0.905);
      }

      const msg = document.getElementById('message-input').value.trim();
      if (msg) {
        ctx.font        = `400 ${Math.round(w * 0.02)}px ${font}`;
        ctx.globalAlpha = 0.75;
        ctx.fillText(msg, w / 2, h * 0.935);
      }

      if (typo.showCoords && currentLat !== null) {
        const latStr = Math.abs(currentLat).toFixed(4) + (currentLat >= 0 ? '° N' : '° S');
        const lonStr = Math.abs(currentLon).toFixed(4) + (currentLon >= 0 ? '° E' : '° W');
        ctx.font        = `400 ${Math.round(w * 0.015)}px ${font}`;
        ctx.globalAlpha = 0.5;
        ctx.fillText(`${latStr}  ·  ${lonStr}`, w / 2, h * 0.958);
      }

      // Attribution
      if (typo.showAttrib) {
        ctx.textAlign   = 'right';
        ctx.font        = `400 ${Math.round(w * 0.012)}px Inter, system-ui, sans-serif`;
        ctx.fillStyle   = colour;
        ctx.globalAlpha = 0.3;
        ctx.fillText('PIGGERY | OSM', w * 0.98, h * 0.987);
      }

      ctx.globalAlpha = 1;

      // Restore layout
      mapArea.style.left   = saved.areaL;
      mapArea.style.width  = saved.areaW;
      mapArea.style.height = saved.areaH;
      frame.style.width        = saved.frameW;
      frame.style.height       = saved.frameH;
      frame.style.aspectRatio  = saved.frameAR;
      map.resize();

      const city = (currentCity || 'map').toLowerCase().replace(/\s+/g, '-');
      canvas.toBlob(blob => {
        const url = URL.createObjectURL(blob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `piggery-print-${city}-${currentSize.label}-200dpi.png`;
        a.click();
        URL.revokeObjectURL(url);
        btn.textContent = '✓ Done';
        setTimeout(() => { btn.textContent = '⬇ Download Print File (200 DPI)'; btn.disabled = false; }, 2500);
      }, 'image/png');

    } catch (err) {
      console.error('Print export error:', err);
      btn.textContent = '⬇ Download Print File (200 DPI)';
      btn.disabled = false;
      showToast('Export failed — try again');
    }
  });
}

// ── Order form ────────────────────────────────────────
document.getElementById('order-btn').addEventListener('click', () => {
  const panel = document.getElementById('order-panel');
  panel.classList.toggle('hidden');
  document.getElementById('order-btn').textContent =
    panel.classList.contains('hidden') ? 'Enquire About This Map' : '✕ Close';
});

document.getElementById('order-form').addEventListener('submit', function(e) {
  e.preventDefault();
  const zoom  = parseFloat(zoomSlider.value);
  const distM = Math.round(20000 * Math.pow(2, 11 - zoom));
  const dist  = Math.max(2000, Math.min(50000, distM));

  document.getElementById('order-field-summary').value = [
    `City: ${currentCity || 'Not set'}`,
    `Country: ${currentCountry || 'Not set'}`,
    `Theme: ${currentTheme.name} (${currentTheme.id})`,
    `Size: ${currentSize.label} (${currentSize.w} × ${currentSize.h} cm)`,
    document.getElementById('message-input').value.trim()
      ? `Message: ${document.getElementById('message-input').value.trim()}` : null,
    currentLat ? `Coordinates: ${currentLat.toFixed(5)}, ${currentLon.toFixed(5)}` : null,
    `Zoom: ${zoom}`,
    `Approx distance: ${dist}m`,
  ].filter(Boolean).join('\n');

  this.submit();
});

// ═══════════════════════════════════════════════════════════
//  GOAT FEATURES
// ═══════════════════════════════════════════════════════════

// ── Additional state ──────────────────────────────────────
const effects = { vignette: 0, brightness: 100, contrast: 100, saturation: 100, buildings3d: false };
const layers  = { buildings: true, parks: true, water: true, railways: true, roads: true };
let currentFrame    = 'none';
let currentBearing  = 0;
let currentNominatimResult = null; // for snap-to-bounds

// ── Shopify product handle ────────────────────────────────
// Single product for all themes — theme/colours/etc. captured in line item properties
const SHOPIFY_PRODUCT_HANDLE = 'custom-map-poster';

// ── State serialisation ───────────────────────────────────
const SHARE_DEFAULTS = {
  themeId: 'starlit_depth',
  sizeLabel: 'A3',
  zoom: 11,
  bearing: 0,
  message: '',
  typo: {
    font: 'Inter',
    colour: null,
    cityScale: 1,
    subScale: 1,
    showCoords: true,
    showCountry: true,
    showAttrib: true,
    customCity: '',
    customCountry: '',
  },
  effects: { vignette: 0, brightness: 100, contrast: 100, saturation: 100, buildings3d: false },
  layers: { buildings: true, parks: true, water: true, railways: true, roads: true },
  roadWeights: {
    'road-motorway': 4,
    'road-primary': 3,
    'road-secondary': 2,
    'road-tertiary': 1.5,
    'road-minor': 0.8,
  },
  frame: 'none',
  orientation: 'portrait',
};

function roundShareNumber(value, places = 6) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Number(value.toFixed(places))
    : value;
}

function addChanged(target, key, value, defaultValue) {
  if (value !== undefined && value !== defaultValue) target[key] = value;
}

function compactSharedState(state) {
  const packed = { v: 3 };

  if (state.city) packed.c = state.city;
  if (state.country) packed.co = state.country;
  if (state.lat !== null && state.lat !== undefined) packed.la = roundShareNumber(state.lat);
  if (state.lon !== null && state.lon !== undefined) packed.lo = roundShareNumber(state.lon);

  addChanged(packed, 'z', roundShareNumber(state.zoom, 2), SHARE_DEFAULTS.zoom);
  addChanged(packed, 'b', roundShareNumber(state.bearing, 1), SHARE_DEFAULTS.bearing);
  addChanged(packed, 't', state.themeId, SHARE_DEFAULTS.themeId);
  addChanged(packed, 'sz', state.sizeLabel, SHARE_DEFAULTS.sizeLabel);
  if (state.message) packed.m = state.message;

  const ty = {};
  addChanged(ty, 'fn', state.typo?.font, SHARE_DEFAULTS.typo.font);
  addChanged(ty, 'clr', state.typo?.colour, SHARE_DEFAULTS.typo.colour);
  addChanged(ty, 'cs', state.typo?.cityScale, SHARE_DEFAULTS.typo.cityScale);
  addChanged(ty, 'ss', state.typo?.subScale, SHARE_DEFAULTS.typo.subScale);
  addChanged(ty, 'sc', state.typo?.showCoords, SHARE_DEFAULTS.typo.showCoords);
  addChanged(ty, 'sy', state.typo?.showCountry, SHARE_DEFAULTS.typo.showCountry);
  addChanged(ty, 'sa', state.typo?.showAttrib, SHARE_DEFAULTS.typo.showAttrib);
  if (state.typo?.customCity) ty.cc = state.typo.customCity;
  if (state.typo?.customCountry) ty.cn = state.typo.customCountry;
  if (Object.keys(ty).length) packed.ty = ty;

  const ef = {};
  addChanged(ef, 'vg', state.effects?.vignette, SHARE_DEFAULTS.effects.vignette);
  addChanged(ef, 'br', state.effects?.brightness, SHARE_DEFAULTS.effects.brightness);
  addChanged(ef, 'ct', state.effects?.contrast, SHARE_DEFAULTS.effects.contrast);
  addChanged(ef, 'st', state.effects?.saturation, SHARE_DEFAULTS.effects.saturation);
  addChanged(ef, 'bd', state.effects?.buildings3d, SHARE_DEFAULTS.effects.buildings3d);
  if (Object.keys(ef).length) packed.e = ef;

  const ly = {};
  addChanged(ly, 'b', state.layers?.buildings, SHARE_DEFAULTS.layers.buildings);
  addChanged(ly, 'p', state.layers?.parks, SHARE_DEFAULTS.layers.parks);
  addChanged(ly, 'w', state.layers?.water, SHARE_DEFAULTS.layers.water);
  addChanged(ly, 'r', state.layers?.railways, SHARE_DEFAULTS.layers.railways);
  addChanged(ly, 'rd', state.layers?.roads, SHARE_DEFAULTS.layers.roads);
  if (Object.keys(ly).length) packed.l = ly;

  const rw = {};
  addChanged(rw, 'mw', state.roadWeights?.['road-motorway'], SHARE_DEFAULTS.roadWeights['road-motorway']);
  addChanged(rw, 'pr', state.roadWeights?.['road-primary'], SHARE_DEFAULTS.roadWeights['road-primary']);
  addChanged(rw, 'se', state.roadWeights?.['road-secondary'], SHARE_DEFAULTS.roadWeights['road-secondary']);
  addChanged(rw, 'te', state.roadWeights?.['road-tertiary'], SHARE_DEFAULTS.roadWeights['road-tertiary']);
  addChanged(rw, 'mi', state.roadWeights?.['road-minor'], SHARE_DEFAULTS.roadWeights['road-minor']);
  if (Object.keys(rw).length) packed.rw = rw;

  addChanged(packed, 'f', state.frame, SHARE_DEFAULTS.frame);
  addChanged(packed, 'o', state.orientation, SHARE_DEFAULTS.orientation);

  return packed;
}

function expandSharedState(packed) {
  if (!packed || packed.v !== 3) return packed;
  return {
    v: 2,
    city: packed.c || '',
    country: packed.co || '',
    lat: packed.la ?? null,
    lon: packed.lo ?? null,
    zoom: packed.z ?? SHARE_DEFAULTS.zoom,
    bearing: packed.b ?? SHARE_DEFAULTS.bearing,
    themeId: packed.t || SHARE_DEFAULTS.themeId,
    sizeLabel: packed.sz || SHARE_DEFAULTS.sizeLabel,
    message: packed.m || '',
    typo: {
      font: packed.ty?.fn ?? SHARE_DEFAULTS.typo.font,
      colour: packed.ty?.clr ?? SHARE_DEFAULTS.typo.colour,
      cityScale: packed.ty?.cs ?? SHARE_DEFAULTS.typo.cityScale,
      subScale: packed.ty?.ss ?? SHARE_DEFAULTS.typo.subScale,
      showCoords: packed.ty?.sc ?? SHARE_DEFAULTS.typo.showCoords,
      showCountry: packed.ty?.sy ?? SHARE_DEFAULTS.typo.showCountry,
      showAttrib: packed.ty?.sa ?? SHARE_DEFAULTS.typo.showAttrib,
      customCity: packed.ty?.cc ?? SHARE_DEFAULTS.typo.customCity,
      customCountry: packed.ty?.cn ?? SHARE_DEFAULTS.typo.customCountry,
    },
    effects: {
      vignette: packed.e?.vg ?? SHARE_DEFAULTS.effects.vignette,
      brightness: packed.e?.br ?? SHARE_DEFAULTS.effects.brightness,
      contrast: packed.e?.ct ?? SHARE_DEFAULTS.effects.contrast,
      saturation: packed.e?.st ?? SHARE_DEFAULTS.effects.saturation,
      buildings3d: packed.e?.bd ?? SHARE_DEFAULTS.effects.buildings3d,
    },
    layers: {
      buildings: packed.l?.b ?? SHARE_DEFAULTS.layers.buildings,
      parks: packed.l?.p ?? SHARE_DEFAULTS.layers.parks,
      water: packed.l?.w ?? SHARE_DEFAULTS.layers.water,
      railways: packed.l?.r ?? SHARE_DEFAULTS.layers.railways,
      roads: packed.l?.rd ?? SHARE_DEFAULTS.layers.roads,
    },
    roadWeights: {
      'road-motorway': packed.rw?.mw ?? SHARE_DEFAULTS.roadWeights['road-motorway'],
      'road-primary': packed.rw?.pr ?? SHARE_DEFAULTS.roadWeights['road-primary'],
      'road-secondary': packed.rw?.se ?? SHARE_DEFAULTS.roadWeights['road-secondary'],
      'road-tertiary': packed.rw?.te ?? SHARE_DEFAULTS.roadWeights['road-tertiary'],
      'road-minor': packed.rw?.mi ?? SHARE_DEFAULTS.roadWeights['road-minor'],
    },
    frame: packed.f || SHARE_DEFAULTS.frame,
    orientation: packed.o || SHARE_DEFAULTS.orientation,
  };
}

function encodeSharedObject(value) {
  return btoa(unescape(encodeURIComponent(JSON.stringify(value))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function decodeSharedObject(value) {
  let encoded = value.replace(/^#/, '').replace(/-/g, '+').replace(/_/g, '/');
  encoded += '='.repeat((4 - encoded.length % 4) % 4);
  return JSON.parse(decodeURIComponent(escape(atob(encoded))));
}

function captureState() {
  return {
    v: 2,
    city: currentCity, country: currentCountry, lat: currentLat, lon: currentLon,
    zoom: map ? map.getZoom() : 11,
    bearing: map ? map.getBearing() : 0,
    themeId: currentTheme.id,
    sizeLabel: currentSize.label,
    message: messageInput ? messageInput.value : '',
    typo: { ...typo },
    effects: { ...effects },
    layers: { ...layers },
    roadWeights: { ...roadWeights },
    frame: currentFrame,
    orientation: document.querySelector('.orient-btn.active')?.dataset.orient || 'portrait',
  };
}

function applyState(s, animate = false) {
  if (!s || s.v !== 2) return;
  // Theme
  if (s.themeId && THEMES[s.themeId]) {
    currentTheme = THEMES[s.themeId];
    highlightActiveSwatch();
    if (mapReady) applyTheme(currentTheme);
    syncRoadEditorToTheme(currentTheme);
  }
  // Location
  if (s.lat && s.lon) {
    currentCity = s.city || ''; currentCountry = s.country || '';
    currentLat = s.lat; currentLon = s.lon;
    citySearch.value = s.city || '';
    if (mapReady) {
      const fn = animate ? 'flyTo' : 'jumpTo';
      map[fn]({ center: [s.lon, s.lat], zoom: s.zoom || 11, bearing: s.bearing || 0 });
    }
  }
  // Message
  if (s.message !== undefined && messageInput) messageInput.value = s.message;
  // Typography
  if (s.typo) {
    Object.assign(typo, s.typo);
    const fs = document.getElementById('font-select');
    if (fs) fs.value = typo.font;
    const tc = document.getElementById('text-colour');
    if (tc) tc.value = typo.colour || currentTheme.text;
    const cs = document.getElementById('city-size');
    if (cs) { cs.value = typo.cityScale; document.getElementById('city-size-val').textContent = typo.cityScale.toFixed(2) + '×'; }
    const ss = document.getElementById('sub-size');
    if (ss) { ss.value = typo.subScale; document.getElementById('sub-size-val').textContent = typo.subScale.toFixed(2) + '×'; }
    const cc = document.getElementById('custom-city');
    if (cc) cc.value = typo.customCity || '';
    const cr = document.getElementById('custom-country');
    if (cr) cr.value = typo.customCountry || '';
    const showC = document.getElementById('show-coords');
    if (showC) showC.checked = typo.showCoords !== false;
    const showCo = document.getElementById('show-country');
    if (showCo) showCo.checked = typo.showCountry !== false;
    const showA = document.getElementById('show-attrib');
    if (showA) showA.checked = typo.showAttrib !== false;
  }
  // Effects
  if (s.effects) {
    Object.assign(effects, s.effects);
    applyEffects();
    syncEffectsUI();
  }
  // Layers
  if (s.layers) {
    Object.assign(layers, s.layers);
    Object.keys(layers).forEach(k => { if (mapReady) setLayerVisibility(k, layers[k]); });
    syncLayersUI();
  }
  // Road weights
  if (s.roadWeights) {
    Object.assign(roadWeights, s.roadWeights);
    document.querySelectorAll('.road-weight[data-layer]').forEach(slider => {
      const layer = slider.dataset.layer;
      if (roadWeights[layer] === undefined) return;
      slider.value = roadWeights[layer];
      if (slider.nextElementSibling) slider.nextElementSibling.textContent = roadWeights[layer];
      if (mapReady) {
        const base = roadWeights[layer];
        map.setPaintProperty(layer, 'line-width',
          ['interpolate', ['linear'], ['zoom'], 10, base * 0.3, 14, base, 16, base * 2]);
      }
    });
  }
  // Frame
  if (s.frame) applyFrame(s.frame);
  // Bearing
  if (s.bearing !== undefined && mapReady) {
    map.setBearing(s.bearing);
    const bs = document.getElementById('bearing-slider');
    if (bs) { bs.value = Math.round(s.bearing); document.getElementById('bearing-value').textContent = Math.round(s.bearing) + '°'; }
  }
  // Size
  if (s.sizeLabel) {
    document.querySelectorAll('.size-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.label === s.sizeLabel);
      if (b.dataset.label === s.sizeLabel) {
        currentSize = { label: b.dataset.label, w: parseFloat(b.dataset.w), h: parseFloat(b.dataset.h), ratio: parseFloat(b.dataset.ratio) };
        const frame = document.getElementById('poster-frame');
        frame.style.aspectRatio = `1 / ${1 / currentSize.ratio}`;
        frame.style.height = `min(calc(100vh - 48px), calc((100vw - var(--sidebar-w) - 48px) * ${1 / currentSize.ratio}))`;
      }
    });
  }
  updateOverlay();
  if (mapReady) { updateAreaCoverage(); updateCompass(); }
}

// ── URL sharing ───────────────────────────────────────────
function encodeState() {
  try { return '#' + encodeSharedObject(compactSharedState(captureState())); }
  catch { return ''; }
}

function decodeState(hash) {
  try { return expandSharedState(decodeSharedObject(hash)); }
  catch { return null; }
}

function getDesignRecordEndpoint() {
  return (window.PIGGERY_DESIGN_RECORD_ENDPOINT || '').replace(/\/+$/, '');
}

function designRecordURL(designId) {
  return `${location.origin}${location.pathname}?design=${encodeURIComponent(designId)}`;
}

async function saveDesignRecord(source = 'preview', status = 'draft') {
  const endpoint = getDesignRecordEndpoint();
  if (!endpoint) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2500);
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        state: captureState(),
        source,
        status,
        metadata: {
          page: location.href,
          user_agent: navigator.userAgent,
        },
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function loadDesignRecordFromURL() {
  const designId = new URLSearchParams(location.search).get('design');
  const endpoint = getDesignRecordEndpoint();
  if (!designId || !endpoint) return false;

  try {
    const res = await fetch(`${endpoint}/${encodeURIComponent(designId)}?public=1`);
    if (!res.ok) return false;
    const record = await res.json();
    if (record && record.state) {
      applyState(record.state, true);
      showToast(`Design ${designId} loaded`);
      return true;
    }
  } catch (_) {}

  return false;
}

document.getElementById('share-btn').addEventListener('click', async () => {
  const btn = document.getElementById('share-btn');
  const original = btn.innerHTML;
  btn.textContent = 'Preparing link...';

  const saved = await saveDesignRecord('preview', 'draft');
  const url = saved?.design_id ? designRecordURL(saved.design_id) : location.origin + location.pathname + encodeState();

  navigator.clipboard.writeText(url).then(() => {
    showToast(saved?.design_id ? `Short link copied: ${saved.design_id}` : 'Link copied — share it with anyone!');
    btn.textContent = '✓ Link Copied!';
    setTimeout(() => { btn.innerHTML = original; }, 2500);
  }).catch(() => prompt('Copy this link:', url));
});

// ── LocalStorage auto-save ────────────────────────────────
let _saveTimer = null;
function scheduleSave() {
  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    try { localStorage.setItem('piggery_v2_design', JSON.stringify(captureState())); } catch (_) {}
  }, 1200);
}

function loadHashDesign(animate = false) {
  if (location.hash && location.hash.length > 1) {
    const s = decodeState(location.hash);
    if (s) { applyState(s, animate); showToast('Design loaded from shared link'); return true; }
  }
  return false;
}

async function loadURLDesign(animate = false) {
  if (loadHashDesign(animate)) return true;
  if (await loadDesignRecordFromURL()) return true;
  return false;
}

async function loadSavedDesign() {
  if (await loadURLDesign(true)) return;

  // Then localStorage
  try {
    const raw = localStorage.getItem('piggery_v2_design');
    if (raw) {
      const s = JSON.parse(raw);
      if (s && confirm('Restore your previous design?')) applyState(s, false);
    }
  } catch (_) {}
}

// ── Undo / redo ───────────────────────────────────────────
const _history = [];
let _histIdx = -1;
const MAX_HIST = 25;

function pushHistory() {
  _history.splice(_histIdx + 1);
  _history.push(captureState());
  if (_history.length > MAX_HIST) _history.shift();
  _histIdx = _history.length - 1;
}

function undo() {
  if (_histIdx <= 0) return;
  _histIdx--;
  applyState(_history[_histIdx]);
  flashUndo('Undo');
}

function redo() {
  if (_histIdx >= _history.length - 1) return;
  _histIdx++;
  applyState(_history[_histIdx]);
  flashUndo('Redo');
}

function flashUndo(label) {
  let el = document.getElementById('undo-indicator');
  if (!el) {
    el = document.createElement('div');
    el.id = 'undo-indicator';
    document.body.appendChild(el);
  }
  el.textContent = label;
  el.classList.add('visible');
  setTimeout(() => el.classList.remove('visible'), 900);
}

// ── Keyboard shortcuts ────────────────────────────────────
document.addEventListener('keydown', e => {
  // Undo/Redo always active
  if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return; }
  // Share
  if ((e.ctrlKey || e.metaKey) && e.key === 'l') { e.preventDefault(); document.getElementById('share-btn').click(); return; }
  // Skip if typing
  if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
  if (!mapReady) return;
  // Zoom
  if (e.key === '+' || e.key === '=') map.zoomIn();
  else if (e.key === '-') map.zoomOut();
  // Reset bearing
  else if (e.key === 'n' || e.key === 'N') map.setBearing(0);
  // Cycle themes
  else if (e.key === 'ArrowRight' && !e.ctrlKey) {
    const keys = Object.keys(THEMES).filter(k => THEMES[k].available !== false); const i = keys.indexOf(currentTheme.id);
    selectTheme(keys[(i + 1) % keys.length]);
  } else if (e.key === 'ArrowLeft' && !e.ctrlKey) {
    const keys = Object.keys(THEMES).filter(k => THEMES[k].available !== false); const i = keys.indexOf(currentTheme.id);
    selectTheme(keys[(i - 1 + keys.length) % keys.length]);
  }
  // Rotate map
  else if (e.key === 'r') map.setBearing(map.getBearing() + 15);
  else if (e.key === 'R') map.setBearing(map.getBearing() - 15);
});

// ── Map bearing / rotation ────────────────────────────────
document.getElementById('map-controls-toggle').addEventListener('click', () => {
  const p = document.getElementById('map-controls-panel');
  p.classList.toggle('hidden');
  document.getElementById('map-controls-toggle').classList.toggle('open', !p.classList.contains('hidden'));
});

document.getElementById('bearing-slider').addEventListener('input', e => {
  const b = parseInt(e.target.value);
  document.getElementById('bearing-value').textContent = b + '°';
  if (mapReady) map.setBearing(b);
});

document.getElementById('bearing-reset-btn').addEventListener('click', () => {
  if (mapReady) map.setBearing(0);
  document.getElementById('bearing-slider').value = 0;
  document.getElementById('bearing-value').textContent = '0°';
});

function updateCompass() {
  const b = map ? map.getBearing() : 0;
  const cr = document.getElementById('compass-rose');
  if (cr) cr.style.transform = `rotate(${-b}deg)`;
  const bs = document.getElementById('bearing-slider');
  if (bs) { bs.value = Math.round(b); document.getElementById('bearing-value').textContent = Math.round(b) + '°'; }
}

// ── Area coverage ─────────────────────────────────────────
function updateAreaCoverage() {
  if (!mapReady) return;
  const bounds = map.getBounds();
  const R = 6371;
  const lat = (bounds.getNorth() + bounds.getSouth()) / 2 * Math.PI / 180;
  const dLat = (bounds.getNorth() - bounds.getSouth()) * Math.PI / 180;
  const dLon = (bounds.getEast() - bounds.getWest()) * Math.PI / 180;
  const h = Math.max(1, Math.round(R * dLat));
  const w = Math.max(1, Math.round(R * Math.cos(lat) * dLon));

  const sidebar = document.getElementById('area-coverage');
  if (sidebar) sidebar.textContent = `~${w} × ${h} km`;

  const badge = document.getElementById('coverage-badge');
  if (badge) { badge.textContent = `~${w} × ${h} km`; badge.style.display = 'block'; }
}

document.getElementById('snap-bounds-btn').addEventListener('click', () => {
  if (!currentLat || !currentLon) { showToast('Search for a city first'); return; }
  if (currentNominatimResult?.boundingbox) {
    const [s, n, w, e] = currentNominatimResult.boundingbox.map(Number);
    map.fitBounds([[w, s], [e, n]], { padding: 20, duration: 1000 });
  } else {
    map.flyTo({ center: [currentLon, currentLat], zoom: 12, duration: 1000 });
  }
});

// ── Visual effects ────────────────────────────────────────
document.getElementById('effects-toggle').addEventListener('click', () => {
  const p = document.getElementById('effects-panel');
  p.classList.toggle('hidden');
  document.getElementById('effects-toggle').classList.toggle('open', !p.classList.contains('hidden'));
});

function applyEffects() {
  const mapEl = document.getElementById('map');
  if (mapEl) mapEl.style.filter = `brightness(${effects.brightness}%) contrast(${effects.contrast}%) saturate(${effects.saturation}%)`;
  const vig = document.getElementById('vignette-overlay');
  if (vig) {
    vig.style.background = `radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,${effects.vignette}) 100%)`;
    vig.style.opacity = effects.vignette > 0 ? '1' : '0';
  }
}

function syncEffectsUI() {
  const vs = document.getElementById('vignette-slider'); if (vs) vs.value = effects.vignette;
  const vv = document.getElementById('vignette-value');  if (vv) vv.textContent = effects.vignette > 0 ? effects.vignette.toFixed(2) : 'Off';
  const bs = document.getElementById('brightness-slider'); if (bs) bs.value = effects.brightness;
  const bv = document.getElementById('brightness-value');  if (bv) bv.textContent = effects.brightness + '%';
  const cs = document.getElementById('contrast-slider');   if (cs) cs.value = effects.contrast;
  const cv = document.getElementById('contrast-value');    if (cv) cv.textContent = effects.contrast + '%';
  const ss = document.getElementById('saturation-slider'); if (ss) ss.value = effects.saturation;
  const sv = document.getElementById('saturation-value');  if (sv) sv.textContent = effects.saturation + '%';
  const b3 = document.getElementById('buildings-3d');      if (b3) b3.checked = effects.buildings3d;
}

document.getElementById('vignette-slider').addEventListener('input', e => {
  effects.vignette = parseFloat(e.target.value);
  document.getElementById('vignette-value').textContent = effects.vignette > 0 ? effects.vignette.toFixed(2) : 'Off';
  applyEffects(); scheduleSave();
});
document.getElementById('brightness-slider').addEventListener('input', e => {
  effects.brightness = parseInt(e.target.value);
  document.getElementById('brightness-value').textContent = effects.brightness + '%';
  applyEffects(); scheduleSave();
});
document.getElementById('contrast-slider').addEventListener('input', e => {
  effects.contrast = parseInt(e.target.value);
  document.getElementById('contrast-value').textContent = effects.contrast + '%';
  applyEffects(); scheduleSave();
});
document.getElementById('saturation-slider').addEventListener('input', e => {
  effects.saturation = parseInt(e.target.value);
  document.getElementById('saturation-value').textContent = effects.saturation + '%';
  applyEffects(); scheduleSave();
});
document.getElementById('effects-reset-btn').addEventListener('click', () => {
  effects.vignette = 0; effects.brightness = 100; effects.contrast = 100; effects.saturation = 100;
  applyEffects(); syncEffectsUI(); scheduleSave();
});

// 3D buildings
document.getElementById('buildings-3d').addEventListener('change', e => {
  effects.buildings3d = e.target.checked;
  set3DBuildings(effects.buildings3d);
  scheduleSave();
});

function set3DBuildings(on) {
  if (!mapReady) return;
  if (on) {
    if (!map.getLayer('building-3d')) {
      map.addLayer({
        id: 'building-3d', type: 'fill-extrusion',
        source: 'ofm', 'source-layer': 'building', minzoom: 13,
        paint: {
          'fill-extrusion-color': currentTheme.buildings || currentTheme.bg,
          'fill-extrusion-height': ['coalesce', ['get', 'height'], 10],
          'fill-extrusion-base':   ['coalesce', ['get', 'min_height'], 0],
          'fill-extrusion-opacity': 0.75,
        }
      }, 'building-fill');
    }
  } else {
    try { if (map.getLayer('building-3d')) map.removeLayer('building-3d'); } catch (_) {}
  }
}

// ── Layer visibility ──────────────────────────────────────
document.getElementById('layers-toggle').addEventListener('click', () => {
  const p = document.getElementById('layers-panel');
  p.classList.toggle('hidden');
  document.getElementById('layers-toggle').classList.toggle('open', !p.classList.contains('hidden'));
});

const LAYER_MAP = {
  buildings: ['building-fill', 'building-detail-outline'],
  parks:     ['landuse-green', 'landcover-green'],
  water:     ['water-fill', 'waterway-line'],
  railways:  ['railway'],
  roads:     ['road-minor', 'road-tertiary', 'road-secondary', 'road-primary', 'road-motorway'],
};

function setLayerVisibility(key, visible) {
  if (!mapReady) return;
  (LAYER_MAP[key] || []).forEach(id => {
    try { map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none'); } catch (_) {}
  });
}

function syncLayersUI() {
  Object.keys(layers).forEach(k => {
    const el = document.getElementById('layer-' + k);
    if (el) el.checked = layers[k];
  });
}

['buildings','parks','water','railways','roads'].forEach(k => {
  document.getElementById('layer-' + k).addEventListener('change', e => {
    layers[k] = e.target.checked;
    setLayerVisibility(k, layers[k]);
    scheduleSave();
  });
});

// ── Frame styles ──────────────────────────────────────────
function applyFrame(frame) {
  const poster = document.getElementById('poster-frame');
  const classes = ['frame-thin','frame-thick','frame-double','frame-vintage','frame-shadow'];
  poster.classList.remove(...classes);
  if (frame !== 'none') poster.classList.add('frame-' + frame);
  currentFrame = frame;
  document.querySelectorAll('.frame-btn').forEach(b => b.classList.toggle('active', b.dataset.frame === frame));
}

document.querySelectorAll('.frame-btn').forEach(btn => {
  btn.addEventListener('click', () => { applyFrame(btn.dataset.frame); scheduleSave(); pushHistory(); });
});

// ── Smart theme suggestions ───────────────────────────────
const SUGGESTIONS = {
  coastal:  ['tidal_lines', 'tropical_dawn', 'cobalt_monoline'],
  nature:   ['woodland_trace', 'forest_moss', 'jade_orbit'],
  heritage: ['heritage_gold', 'vintage_atlas', 'sandstone_calm'],
  urban:    ['midnight_ink', 'electric_horizon', 'neon_circuit'],
  warm:     ['amber_canopy', 'clay_horizon', 'dusk_ember'],
  minimal:  ['soft_neutral', 'nordic_frost', 'gallery_ink'],
};

function suggestThemes(nominatimResult) {
  currentNominatimResult = nominatimResult;
  document.querySelectorAll('.theme-swatch').forEach(el => {
    el.classList.remove('suggested');
    const b = el.querySelector('.suggest-badge');
    if (b) b.remove();
  });
  const name = (nominatimResult?.display_name || '').toLowerCase();
  const type = (nominatimResult?.type || nominatimResult?.class || '').toLowerCase();
  let set = SUGGESTIONS.minimal;
  if (/bay|beach|coast|port|harbour|sea|ocean|pier|marina/.test(name) || /bay|harbour|marina/.test(type))
    set = SUGGESTIONS.coastal;
  else if (/forest|wood|park|moor|hill|dale|vale|glen|fell/.test(name) || /forest|park|nature/.test(type))
    set = SUGGESTIONS.nature;
  else if (/castle|abbey|cathedral|village|manor|hall/.test(name))
    set = SUGGESTIONS.heritage;
  else if (type === 'city' || type === 'metropolis' || /city centre|city center/.test(name))
    set = SUGGESTIONS.urban;
  else if (/island|mediterranean|tropical/.test(name))
    set = SUGGESTIONS.warm;
  set.forEach(id => {
    const sw = document.querySelector(`.theme-swatch[data-id="${id}"]`);
    if (!sw) return;
    sw.classList.add('suggested');
    const badge = document.createElement('div');
    badge.className = 'suggest-badge';
    badge.textContent = '★';
    sw.appendChild(badge);
  });
  showStatus(`★ Theme suggestions updated for ${nominatimResult?.display_name?.split(',')[0]}`, 3000);
}

// suggestThemes and scheduleSave are called from the existing selectLocation
// by patching it after definition — done via a post-load hook below

// ── Shopify Buy This Map ──────────────────────────────────
document.getElementById('buy-btn').addEventListener('click', async () => {
  const btn        = document.getElementById('buy-btn');
  const origHTML   = btn.innerHTML;
  const handle     = currentTheme.id.replace(/_/g, '-') + '-city-map';
  const link       = encodeState();
  const previewURL = location.origin + '/pages/map-preview' + link;
  const savedDesign = await saveDesignRecord('cart', 'cart');

  const properties = {
    'City':        currentCity    || 'Not specified',
    'Country':     currentCountry || 'Not specified',
    'Theme':       currentTheme.name,
    'Size':        `${currentSize.label} (${currentSize.w}×${currentSize.h}cm)`,
    'Message':     messageInput.value.trim() || '',
    'Coords':      currentLat ? `${currentLat.toFixed(5)},${currentLon.toFixed(5)}` : '',
    'Font':        typo.font,
    'Preview_URL': previewURL,
  };

  if (savedDesign?.design_id) {
    properties.Design_ID = savedDesign.design_id;
    properties.Design_URL = designRecordURL(savedDesign.design_id);
  }

  btn.textContent = 'Adding to cart…';
  btn.disabled = true;

  try {
    // Fetch the product to find the variant ID for the selected size
    const prodRes = await fetch(`${location.origin}/products/${handle}.js`);
    if (!prodRes.ok) throw new Error('Product not found');
    const product = await prodRes.json();

    const variant = product.variants.find(v =>
      v.title === currentSize.label || v.option1 === currentSize.label
    ) || product.variants[0];

    // Add to cart via Shopify AJAX API
    const cartRes = await fetch(`${location.origin}/cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variant.id, quantity: 1, properties }),
    });

    if (!cartRes.ok) throw new Error('Cart add failed');

    // Go straight to checkout
    window.location.href = `${location.origin}/checkout`;

  } catch (err) {
    console.warn('Buy button error:', err);
    btn.innerHTML = origHTML;
    btn.disabled = false;
    alert('Sorry, something went wrong adding this to your cart. Please try again or contact us at piggerystudio@gmail.com');
  }
});

// Auto-save on message / custom text inputs
messageInput.addEventListener('input', scheduleSave);
document.getElementById('custom-city').addEventListener('input', scheduleSave);
document.getElementById('custom-country').addEventListener('input', scheduleSave);

// ── Map event hooks (bearing, coverage, compass) ──────────
function attachMapHooks() {
  map.on('rotate', updateCompass);
  map.on('move',   updateAreaCoverage);
  map.on('moveend', () => { scheduleSave(); pushHistory(); });
  // loadSavedDesign called after map ready (see initMap load handler)
}

// ── Review mode ───────────────────────────────────────────
// Activated when URL contains ?review=1 (or &review=1).
// Hides the editing sidebar, shows a read-only review panel on the right,
// and disables map interaction so customers can't accidentally move the map.

const REVIEW_MODE = new URLSearchParams(location.search).get('review') === '1';

function initReviewMode() {
  if (!REVIEW_MODE) return;

  document.body.classList.add('review-mode');

  // Disable map interaction once ready (customer shouldn't reposition the map)
  const disableInteraction = () => {
    map.dragPan.disable();
    map.scrollZoom.disable();
    map.doubleClickZoom.disable();
    map.touchZoomRotate.disable();
    map.keyboard.disable();
  };
  if (mapReady) { disableInteraction(); }
  else { map.once('load', disableInteraction); }

  const panel = document.getElementById('review-panel');
  panel.classList.remove('hidden');
  populateReviewDetails();

  // Approve button
  document.getElementById('review-approve-btn').addEventListener('click', () => {
    const confirm = document.getElementById('review-confirm');
    confirm.textContent = '✓ Great! We\'ve noted your approval. We\'ll start preparing your print shortly.';
    confirm.classList.remove('hidden');
    document.getElementById('review-actions').classList.add('hidden');
    document.getElementById('review-changes-form').classList.add('hidden');
  });

  // Request changes toggle
  document.getElementById('review-changes-btn').addEventListener('click', () => {
    document.getElementById('review-changes-form').classList.toggle('hidden');
  });

  // Send changes
  document.getElementById('review-send-btn').addEventListener('click', () => {
    const text = document.getElementById('review-changes-text').value.trim();
    if (!text) { showToast('Please describe the changes you\'d like'); return; }

    const subject = encodeURIComponent(`Map revision request — ${currentCity || 'your order'}`);
    const body    = encodeURIComponent(
      `Hi Piggery Studio,\n\nI'd like to request changes to my map:\n\n${text}\n\n` +
      `— Sent from the preview tool (${location.href})`
    );
    window.open(`mailto:piggerystudio@gmail.com?subject=${subject}&body=${body}`);

    const confirm = document.getElementById('review-confirm');
    confirm.textContent = '✓ Your email app has opened with the details pre-filled. Send it to complete your request.';
    confirm.classList.remove('hidden');
  });
}

function populateReviewDetails() {
  const details = document.getElementById('review-details');
  if (!details) return;

  const rows = [
    { label: 'Location', value: currentCity ? `${currentCity}${currentCountry ? ', ' + currentCountry : ''}` : '—' },
    { label: 'Theme', value: currentTheme ? currentTheme.name : '—' },
    { label: 'Size', value: currentSize ? `${currentSize.label} (${currentSize.w} × ${currentSize.h} cm)` : '—' },
    { label: 'Message', value: (messageInput && messageInput.value.trim()) || '—' },
    { label: 'Font', value: typo.font || '—' },
  ];

  details.innerHTML = rows
    .filter(r => r.value !== '—' || r.label === 'Location')
    .map(r => `<div class="detail-row"><strong>${r.label}</strong>${r.value}</div>`)
    .join('');
}

// ── Onboarding tour ──────────────────────────────────────
const TOUR_KEY = 'piggery_tour_v1';

const TOUR_STEPS = [
  {
    target: 'city-wrap',
    title: 'Start here',
    text: 'Search for any city or place in the world. Your map will centre on whatever you find.',
  },
  {
    target: 'theme-grid',
    title: 'Pick a theme',
    text: 'Each theme has a unique colour palette — try a few once your city is loaded to see them come to life.',
  },
  {
    target: 'message-input',
    title: 'Add something personal',
    text: 'A date, a name, a line that means something. It appears at the bottom of your poster.',
  },
  {
    target: 'size-grid',
    title: 'Choose your size',
    text: 'A3 is our most popular. Every size is printed at 300 DPI on premium archival paper.',
  },
  {
    target: 'share-btn',
    title: 'Save your design',
    text: 'Copy your link to come back to this exact design later — or hit Buy when you\'re ready to order.',
  },
];

let _tourStep = 0;
let _tourOverlay = null;
let _tourBubble  = null;

function initTour() {
  if (new URLSearchParams(location.search).get('review') === '1') return;

  // Wrap existing header children so we can add the ? button alongside them
  const header = document.querySelector('.sidebar-header');
  if (header) {
    const wrap = document.createElement('div');
    while (header.firstChild) wrap.appendChild(header.firstChild);
    header.appendChild(wrap);

    const btn = document.createElement('button');
    btn.id = 'tour-help-btn';
    btn.title = 'Show tips';
    btn.textContent = '?';
    btn.addEventListener('click', startTour);
    header.appendChild(btn);
  }

  if (!localStorage.getItem(TOUR_KEY)) {
    setTimeout(startTour, 700);
  }
}

function startTour() {
  endTour();  // clear any existing tour first

  _tourStep = 0;

  _tourOverlay = document.createElement('div');
  _tourOverlay.id = 'tour-overlay';
  document.body.appendChild(_tourOverlay);

  _tourBubble = document.createElement('div');
  _tourBubble.id = 'tour-bubble';
  document.body.appendChild(_tourBubble);

  document.body.classList.add('tour-active');
  showTourStep(0);
}

function showTourStep(index) {
  const step   = TOUR_STEPS[index];
  const target = document.getElementById(step.target);

  document.querySelectorAll('.tour-highlight').forEach(el => el.classList.remove('tour-highlight'));
  if (!target) { advanceTour(); return; }

  target.scrollIntoView({ block: 'center', behavior: 'auto' });
  target.classList.add('tour-highlight');

  const rect      = target.getBoundingClientRect();
  const sidebarEl = document.getElementById('sidebar');
  const bubbleX   = sidebarEl.getBoundingClientRect().right + 16;
  const bubbleY   = Math.max(16, Math.min(
    rect.top + rect.height / 2 - 40,
    window.innerHeight - 220
  ));

  _tourBubble.style.left = bubbleX + 'px';
  _tourBubble.style.top  = bubbleY + 'px';

  const isLast = index === TOUR_STEPS.length - 1;
  _tourBubble.innerHTML = `
    <div class="tour-count">Step ${index + 1} of ${TOUR_STEPS.length}</div>
    <div class="tour-title">${step.title}</div>
    <div class="tour-text">${step.text}</div>
    <div class="tour-actions">
      <button class="tour-btn-skip">Skip tour</button>
      <button class="tour-btn-next">${isLast ? 'Done ✓' : 'Next →'}</button>
    </div>
  `;

  _tourBubble.querySelector('.tour-btn-next').addEventListener('click', advanceTour);
  _tourBubble.querySelector('.tour-btn-skip').addEventListener('click', endTour);
}

function advanceTour() {
  _tourStep++;
  if (_tourStep >= TOUR_STEPS.length) endTour();
  else showTourStep(_tourStep);
}

function endTour() {
  document.querySelectorAll('.tour-highlight').forEach(el => el.classList.remove('tour-highlight'));
  document.body.classList.remove('tour-active');
  if (_tourOverlay) { _tourOverlay.remove(); _tourOverlay = null; }
  if (_tourBubble)  { _tourBubble.remove();  _tourBubble  = null; }
  localStorage.setItem(TOUR_KEY, '1');
}

// ── Mobile layout init ───────────────────────────────────
function initMobileLayout() {
  if (window.innerWidth > 700) return;
  // Map preview is always visible at top; sidebar scrolls below.
  // Just ensure MapLibre knows its container size.
  if (map) setTimeout(() => map.resize(), 100);
}

// ── Use my location ───────────────────────────────────────
document.getElementById('use-location-btn').addEventListener('click', () => {
  const btn = document.getElementById('use-location-btn');
  btn.textContent = '◎ Locating…';
  btn.disabled = true;

  navigator.geolocation.getCurrentPosition(
    async ({ coords: { latitude: lat, longitude: lon } }) => {
      try {
        const url  = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`;
        const res  = await fetch(url, { headers: { 'User-Agent': 'PiggeryStudioPreview/1.0' } });
        const data = await res.json();

        const name    = data.address?.city || data.address?.town || data.address?.village || data.address?.county || 'My Location';
        const country = data.address?.country || '';

        currentCity    = name;
        currentCountry = country;
        currentLat     = lat;
        currentLon     = lon;

        const zoom = parseFloat(zoomSlider.value);
        map.flyTo({ center: [lon, lat], zoom, duration: 1200, essential: true });
        updateOverlay();
        if (typeof suggestThemes === 'function') suggestThemes({ display_name: data.display_name || name, type: data.type || data.class || '' });
        scheduleSave();
        pushHistory();
      } catch {
        showToast('Could not detect location — try searching manually');
      }
      btn.textContent = '◎ Use my location';
      btn.disabled = false;
    },
    () => {
      btn.textContent = '◎ Location access denied';
      btn.disabled = false;
      setTimeout(() => { btn.textContent = '◎ Use my location'; }, 3000);
    },
    { timeout: 10000 }
  );
});

// ── Init ──────────────────────────────────────────────────
buildThemeGrid();
buildSizeGrid();
addCanvasProtection();
syncZoomSlider();
updateOverlay();
loadURLDesign(false);
try {
  initMap();
} catch (err) {
  console.warn('Map preview could not initialise:', err);
  placeholder.classList.remove('hidden');
}
initReviewMode();
initMobileLayout();
initTour();
attachMapHooks();
